import { Link } from 'react-router-dom';
import EventCard from '../components/EventCard';
import Countdown from '../components/Countdown';
import OfflinePill from '../components/OfflinePill';
import PhotoTile from '../components/PhotoTile';
import { SkeletonEvents } from '../components/Skeleton';
import { useScheduleModel } from '../lib/schedule';
import { playlistOf } from '../lib/content';
import { useMedia, visibleTo } from '../lib/media';
import { useSession } from '../lib/session';
import TeamStanding from '../components/TeamStanding';
import PlaylistCard from '../components/PlaylistCard';
import { MarkTrophy } from '../components/Icon';
import { dayKey, fmtDay, untilLabel } from '../lib/time';

/**
 * Now — the landing screen and the most-used one.
 * NOT on the canvas: the "try next" line asks for Now and the map to be drawn,
 * so rather than invent a new visual language this page is assembled from
 * components the artboards already settled — the hero event card from 1d, the
 * tile from 1h. Worth redrawing properly before November.
 */
export default function Now() {
  const model = useScheduleModel();
  const session = useSession();
  const media = useMedia();
  const shared = visibleTo(media);

  if (model.loading) return <div className="page page-narrow"><SkeletonEvents rows={2} /></div>;

  const focus = model.current ?? model.next;
  /**
   * The playlist rides on the reception, whose window already runs to 3am — the
   * after party is the tail of that event rather than one of its own (D57).
   *
   * Shown from whenever the site is first opened until that window closes,
   * which is not as broad as it sounds: there is no pre-trip login (D8), so in
   * practice it appears when a guest arrives and collects songs across the
   * week. Gating it to "reception is next" would have surfaced it at seven in
   * the evening on the last night, having missed almost every chance to add
   * anything.
   *
   * Found by looking for a playlist rather than by naming `reception`, so
   * moving it to another event is a `schedule.json` edit and no deploy (D12).
   */
  const playlistEvent = model.events.find(
    (e) => playlistOf(e) && Date.parse(e.end ?? e.start) > model.at,
  );
  const todayRest = model.events.filter(
    (e) => dayKey(e.start) === dayKey(model.at) && e.id !== focus?.id && model.stateOf(e) !== 'past',
  );

  return (
    <div className="page page-narrow">
      <OfflinePill fetchedAt={model.fetchedAt} />

      {/* Removes itself on 5 November — see `Countdown`. Deliberately above the
          team tag and the hero card: before the week it is the reason to open
          the page, and during the week it is gone, so it costs that space
          nothing. */}
      <Countdown />

      {/* The team's own standing, and the way through to the Scorecard (D56).
          A card rather than the tag that used to sit here: the tag named the
          team and nothing else, so the only reason to tap it was curiosity
          about a screen you had to already know existed. Still not a sixth
          header destination — five labelled tabs are what force the header's
          375px breakpoint, and a sixth would cost the labels everywhere
          (D32). */}
      {(session || playlistEvent) && (
        <div className="stack" style={{ gap: 8, padding: '14px 18px 0' }}>
          {/* No team, no standing. Before a guest claims their name there is
              nothing to be placed against (D60), and a card reading "8th" for a
              team they are not on is worse than no card. */}
          {session?.team && <TeamStanding team={session.team} />}
          {/* Only while it can still be acted on. A playlist you can no longer
              add to is a dead row above the hero card, and this space holds two
              rows at most before "Right now" leaves the first screen. */}
          {playlistEvent && <PlaylistCard playlist={playlistOf(playlistEvent)!} />}
        </div>
      )}

      {focus ? (
        <>
          <div className="day-head">
            <h4>{model.current ? 'Right now' : 'Up next'}</h4>
            <span className="day-note">{fmtDay(focus.start)}</span>
          </div>
          <div className="ev-list">
            <EventCard
              event={focus}
              venue={model.venueOf(focus.venueId)}
              state={model.stateOf(focus)}
              at={model.at}
              density="hero"
            />
          </div>
          {focus.notes && (
            <div className="page-pad" style={{ paddingTop: 14 }}>
              <h5 style={{ margin: '0 0 5px' }}>Notes from us</h5>
              <p style={{ fontSize: 13, margin: 0, opacity: 0.85 }}>{focus.notes}</p>
            </div>
          )}
        </>
      ) : (
        <div className="empty">
          <MarkTrophy className="mark" size={44} />
          <h4 style={{ margin: 0 }}>That's the week</h4>
          <p>
            Thank you for coming all this way. Everything anybody photographed is still here, and it is
            staying here — have a look through it when you land.
          </p>
          <Link className="btn btn-primary" to="/gallery">Open the gallery</Link>
        </div>
      )}

      {todayRest.length > 0 ? (
        <>
          <div className="day-head"><h4>Later today</h4></div>
          <div className="ev-list">
            {todayRest.map((event) => (
              <EventCard
                key={event.id} event={event} venue={model.venueOf(event.venueId)}
                state={model.stateOf(event)} at={model.at} density="row"
              />
            ))}
          </div>
        </>
      ) : focus && model.stateOf(focus) !== 'now' && (
        <p className="page-pad" style={{ fontSize: 13, opacity: 0.6, marginTop: 16 }}>
          Nothing else today — the next thing is {fmtDay(focus.start)}, {untilLabel(focus.start, model.at)}.
        </p>
      )}

      {shared.length > 0 && (
        <>
          <div className="day-head" style={{ marginTop: 8 }}>
            <h4>Coming in</h4>
            <Link to="/gallery" style={{ fontSize: 12, textDecoration: 'none' }}>{shared.length} photos so far</Link>
          </div>
          <div className="strip">
            {shared.slice(0, 12).map((item) => <PhotoTile key={item.id} item={item} />)}
          </div>
        </>
      )}

      <div className="tz-note">All times Khao Lak, GMT+7</div>
    </div>
  );
}
